import { useEffect, useCallback, useRef, useState } from 'react';
import { useNotificationStore } from '@/store/notification.store';
import { useTenantStore } from '@/store/tenant.store';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/services/api';
import { parseApiError, getErrorMessage } from '@/utils/api-errors';
import type { PaginatedNotifications, UserNotification } from '@/types/notification';

const POLL_INTERVAL = parseInt(import.meta.env.VITE_NOTIFICATION_POLL_INTERVAL || '30000', 10); // Configurable, default 30 seconds

export function useNotifications() {
  const {
    notifications,
    unreadCount,
    isLoading,
    lastFetchedAt,
    setNotifications,
    setUnreadCount,
    addNotifications,
    markAsRead,
    markAllAsRead,
    setLoading,
    setError,
    setLastFetchedAt,
    dismiss,
  } = useNotificationStore();

  const { activeTenant } = useTenantStore();
  const { isAuthenticated } = useAuthStore();
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  // Use a ref for lastFetchedAt inside callbacks to avoid re-creating
  // checkForNewNotifications on every poll cycle (which would reset the interval).
  const lastFetchedAtRef = useRef<string | null>(lastFetchedAt);
  useEffect(() => {
    lastFetchedAtRef.current = lastFetchedAt;
  }, [lastFetchedAt]);

  // Fetch notifications from the API
  const fetchNotifications = useCallback(async (signal?: AbortSignal) => {
    if (!activeTenant || !isAuthenticated) return;

    try {
      setLoading(true);
      const response = await api.get<PaginatedNotifications>('/user-notifications', {
        limit: 50,
      }, { signal });
      setNotifications(response.data);
      // Find the newest server timestamp to avoid client clock skew, regardless of sort order
      const serverTimestamp = response.data.length > 0
        ? response.data.reduce((a, b) =>
            new Date(a.created_at) > new Date(b.created_at) ? a : b
          ).created_at
        : new Date().toISOString();
      setLastFetchedAt(serverTimestamp);
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch notifications:', error);
      // WHY: a swallowed failure left `notifications` at [] with no error state, so
      // the panel rendered "No notifications yet / You're all caught up" during a
      // backend outage, 429, or expired-tenant 403. Operators stopped checking while
      // evacuation-order / resource-request / task-assignment notifications sat
      // queued server-side. Record the failure so the UI can say the feed is down.
      setError(getErrorMessage(parseApiError(error)));
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [activeTenant, isAuthenticated, setNotifications, setLoading, setError, setLastFetchedAt]);

  // Fetch unread count only
  const fetchUnreadCount = useCallback(async (signal?: AbortSignal) => {
    if (!activeTenant || !isAuthenticated) return;

    try {
      const response = await api.get<{ count: number }>('/user-notifications/count', undefined, { signal });
      setUnreadCount(response.count);
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch unread count:', error);
      setError(getErrorMessage(parseApiError(error)));
    }
  }, [activeTenant, isAuthenticated, setUnreadCount, setError]);

  // Check for new notifications.
  // Uses lastFetchedAtRef to read the latest value without causing identity changes
  // that would reset the polling interval and trigger immediate re-fetches.
  const checkForNewNotifications = useCallback(async (signal?: AbortSignal) => {
    const currentLastFetched = lastFetchedAtRef.current;
    if (!activeTenant || !isAuthenticated) return;

    // WHY: lastFetchedAt is only ever set by a SUCCESSFUL full fetch. If the initial
    // fetch failed (backend blip / 429 at login) nothing re-triggers it — this used
    // to `return` here on every 30s tick, so in-app notification delivery stayed dead
    // for the rest of the shift unless the operator happened to hit Refresh, switch
    // tenant, or reload. Re-run the full fetch so the poll self-heals.
    if (!currentLastFetched) {
      await fetchNotifications(signal);
      return;
    }

    try {
      const response = await api.get<PaginatedNotifications>('/user-notifications', {
        limit: 10,
      }, { signal });

      // Find notifications newer than last fetch
      const newNotifications = response.data.filter(
        (n: UserNotification) => new Date(n.created_at) > new Date(currentLastFetched)
      );

      if (newNotifications.length > 0) {
        addNotifications(newNotifications);
        const newest = newNotifications.reduce((a, b) =>
          new Date(a.created_at) > new Date(b.created_at) ? a : b
        );
        setLastFetchedAt(newest.created_at);
      }
    } catch (error: any) {
      if (error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to check for new notifications:', error);
    }
    // `fetchNotifications` is identity-stable across the same
    // [activeTenant, isAuthenticated] pair, so adding it here does NOT reintroduce
    // the poll-reset cascade documented below (lastFetchedAt is still read via ref).
  }, [activeTenant, isAuthenticated, addNotifications, setLastFetchedAt, fetchNotifications]);

  // Mark notification as read via API
  const handleMarkAsRead = useCallback(
    async (notificationId: string) => {
      if (!activeTenant) return;

      const prev = useNotificationStore.getState();
      markAsRead(notificationId);

      try {
        await api.post(`/user-notifications/${notificationId}/read`);
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
        useNotificationStore.setState({ notifications: prev.notifications, unreadCount: prev.unreadCount });
      }
    },
    [activeTenant, markAsRead]
  );

  // Mark all as read via API
  const handleMarkAllAsRead = useCallback(async () => {
    if (!activeTenant) return;

    const prev = useNotificationStore.getState();
    markAllAsRead();

    try {
      await api.post('/user-notifications/read-all');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      useNotificationStore.setState({ notifications: prev.notifications, unreadCount: prev.unreadCount });
    }
  }, [activeTenant, markAllAsRead]);

  // Dismiss notification via API
  const handleDismiss = useCallback(
    async (notificationId: string) => {
      if (!activeTenant) return;

      const prev = useNotificationStore.getState();
      dismiss(notificationId);

      try {
        await api.post(`/user-notifications/${notificationId}/dismiss`);
      } catch (error) {
        console.error('Failed to dismiss notification:', error);
        useNotificationStore.setState({ notifications: prev.notifications, unreadCount: prev.unreadCount });
      }
    },
    [activeTenant, dismiss]
  );

  // Initial fetch — abort in-flight request on tenant switch or unmount
  useEffect(() => {
    if (!activeTenant || !isAuthenticated) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchNotifications(controller.signal);

    return () => {
      controller.abort();
    };
  }, [activeTenant, isAuthenticated, fetchNotifications]);

  // Track page visibility to pause polling when tab is inactive
  const [isVisible, setIsVisible] = useState(!document.hidden);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Set up polling (only when tab is visible) — abort on teardown.
  // Note: lastFetchedAt is intentionally read via ref (lastFetchedAtRef) inside
  // checkForNewNotifications to avoid resetting the interval every time new
  // notifications are found. Without this, each poll that finds new data would
  // update lastFetchedAt -> recreate checkForNewNotifications -> re-run this
  // effect -> immediately call checkForNewNotifications again (a cascade).
  useEffect(() => {
    if (!activeTenant || !isAuthenticated || !isVisible) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    const controller = new AbortController();

    // Check immediately when becoming visible again
    if (isVisible && lastFetchedAtRef.current) {
      checkForNewNotifications(controller.signal);
    }

    // Start polling
    pollIntervalRef.current = setInterval(() => {
      checkForNewNotifications(controller.signal);
    }, POLL_INTERVAL);

    return () => {
      controller.abort();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [activeTenant, isAuthenticated, isVisible, checkForNewNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    dismiss: handleDismiss,
  };
}
